import React, { useState, useEffect, useCallback } from "react";
import { TextField, Paper, Button, Snackbar, LinearProgress, Typography, SnackbarContent, Grid, Box, Divider } from "@mui/material";
import { styled } from '@mui/material/styles';
import { useSelector, useDispatch } from "react-redux";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import {
  forgotPassword as forgotPasswordAction,
  confirmForgotPassword as confirmForgotPasswordAction,
} from "../../actions/auth_actions";
import { AUTH_ERROR_CLEAR } from "../../actions/types";
import { ReactComponent as SmLogo } from "../Common/Logo/smLogo.svg";
import { ReactComponent as Mountains } from "./Icons/mountains.svg";
import * as ROUTES from "../../constants/routes";

// --- Styled Components to match Login.js/Signup.js ---

const RootContainer = styled(Box)(({ theme }) => ({
  flexGrow: 1,
}));

const LoginPaper = styled(Paper)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  minWidth: 720,
  maxWidth: 720,
  [theme.breakpoints.down(740)]: {
    maxWidth: 600,
    minWidth: 600,
  },
  [theme.breakpoints.down('sm')]: {
    minWidth: '100%',
    width: '100%',
  },
}));

const FormContainer = styled(Box)(({ theme }) => ({
  marginLeft: theme.spacing(4),
  marginRight: '27px',
  marginTop: theme.spacing(4),
  marginBottom: theme.spacing(1),
  [theme.breakpoints.down('sm')]: {
    margin: theme.spacing(2),
  },
}));

const MountainsGrid = styled(Grid)(({ theme }) => ({
  backgroundColor: "#FDEFE7",
  borderTopRightRadius: 8,
  borderBottomRightRadius: 8,
  overflow: "hidden",
  display: "flex",
  '& .mountains-svg': {
    width: "105%",
    marginLeft: -1,
    alignSelf: "flex-end",
    marginBottom: -15,
    [theme.breakpoints.down("sm")]: {
      width: "160%",
      height: "160%",
      marginBottom: -20,
      alignSelf: "flex-start",
    },
  }
}));

const Forget = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { error:errorMessage, forgotPasswordCode } = useSelector(state => state.auth);

  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [passwordcon, setPasswordcon] = useState("");
  const [open, setOpen] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleClose = useCallback(() => {
    setOpen(false);
    dispatch({ type: AUTH_ERROR_CLEAR });
  }, [dispatch]);

  useEffect(() => {
    if (errorMessage && errorMessage !== "" && errorMessage !== error) {
      setOpen(true);
      setError(errorMessage);
      setLoading(false);
    }
  }, [errorMessage]);

  const handleChange = (prop) => (event) => {
    const value = event.target.value;
    switch (prop) {
      case "email":
        setEmail(value);
        break;
      case "code":
        setCode(value);
        break;
      case "password":
        setPassword(value);
        break;
      case "passwordcon":
        setPasswordcon(value);
        break;
      default:
        break;
    }
  };

  const reset = (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setOpen(false);

    const username = email.trim();

    if (!forgotPasswordCode) {
      dispatch(forgotPasswordAction({ username }, navigate));
    } else {
      if (password === passwordcon) {
        dispatch(
          confirmForgotPasswordAction(
            { username, code: code.trim(), newPassword: password },
            navigate
          )
        );
      } else {
        setError("Passwords should match!");
        setOpen(true);
        setLoading(false);
      }
    }
    setLoading(false);
  };

  const isRightLength = password && password.length >= 8;
  const hasSpecial = /[/\W|_/g]/.test(password);
  const hasNumber = password.match(/\d/);
  const hasUpperCase = password.match(/[A-Z]/);

  const textFieldProps = {
    fullWidth: true,
    size: "small",
    variant: "outlined",
    InputProps: {
      sx: { fontSize: 14, backgroundColor: "#F5F6F7", '& .MuiOutlinedInput-notchedOutline': { border: 'none' } },
    },
    sx: { mt: 0, mb: 0 }
  };

  return (
    <RootContainer>
      <Box sx={{ height: { xs: 2, md: 3 } }}>
        {loading && <LinearProgress />}
      </Box>

      <Grid container spacing={0} direction="column" justifyContent="center" alignItems="center" sx={{ minHeight: "100vh" }}>
        <Grid item sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
           <Box sx={{
                display: 'flex',
                justifyContent: 'center',
                maxHeight: { sm: 160 },
                width: { xs: '80%', sm: '100%' },
                mt: { xs: '15px', sm: 0 },
            }}>
                <SmLogo style={{ width: '100%', height: 'auto' }} />
            </Box>
        </Grid>
        <Grid item sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
          <LoginPaper>
            <Grid container spacing={0} direction="row-reverse" justifyContent="center" alignItems="stretch" sx={{ minHeight: 480 }}>
              <MountainsGrid item size={{ sm: 5 }} sx={{ display: { xs: 'none', sm: 'flex' } }}>
                <Mountains className="mountains-svg" />
              </MountainsGrid>
              <Grid item size={{ xs: 12, sm: 7 }}>
                {!forgotPasswordCode ? (
                  <FormContainer>
                    <Typography variant="h4" sx={{ fontSize: { xs: 25, sm: 32 }, pb: 1.25 }}>
                      Forgotten your password?
                    </Typography>
                    <Typography variant="body1" sx={{ color: "#5A6375", fontSize: 14, mt: 0.375, mb: 2 }}>
                      Enter your email address below and we’ll send you details of how to get back into Skillsminer.
                    </Typography>
                    <Box component="form" onSubmit={reset} sx={{ width: '100%' }}>
                      <Typography variant="body1" sx={{ color: "#202A3E", fontSize: 14, mb: '4px', mt: '18px' }}>
                        Email:
                      </Typography>
                      <TextField
                        {...textFieldProps}
                        id="outlined-email-input"
                        placeholder="name@email.com"
                        type="email"
                        name="email"
                        autoComplete="email"
                        autoFocus
                        required
                        onChange={handleChange("email")}
                      />
                      <Button
                        variant="contained"
                        sx={{ mt: { xs: 2, sm: 2 }, mb: { xs: '14px', sm: 2 } }}
                        size="large"
                        color="primary"
                        type="submit"
                        fullWidth
                      >
                        Reset
                      </Button>
                    </Box>
                    <Button
                      variant="outlined"
                      id="back-btn"
                      sx={{ mt: { xs: 2, sm: 2 }, mb: { xs: '14px', sm: 2 } }}
                      size="large"
                      component={RouterLink}
                      to={"/login"}
                      fullWidth
                    >
                      Back
                    </Button>
                  </FormContainer>
                ) : (
                  <FormContainer>
                    <Typography variant="h4" sx={{ fontSize: { xs: 25, sm: 32 } }}>
                      New Password
                    </Typography>
                    <Typography variant="body1" sx={{ color: "#5A6375", fontSize: 14, mt: 0.375, mb: 2 }}>
                      Please enter the verification code we sent to{" "}
                      <strong>{email}</strong>
                    </Typography>
                    <Box component="form" onSubmit={reset} sx={{ width: '100%' }}>
                      <Typography variant="body1" sx={{ color: "#202A3E", fontSize: 14, mb: '4px', mt: '18px' }}>
                        Verification Code
                      </Typography>
                      <TextField
                        {...textFieldProps}
                        id="outlined-code-input"
                        type="text"
                        name="code"
                        placeholder="Code"
                        autoComplete="off"
                        required
                        onChange={handleChange("code")}
                      />
                      <Typography variant="body1" sx={{ color: "#202A3E", fontSize: 14, mb: '4px', mt: '18px' }}>
                        New password
                      </Typography>
                      {password.length > 0 && (
                        <Paper sx={{ mb: "5px", p: 1, backgroundColor: "#faebd7" }}>
                          <Grid container>
                            {[
                              { label: '1 uppercase character', valid: hasUpperCase }, { label: '1 number', valid: hasNumber },
                              { label: '1 symbol', valid: hasSpecial }, { label: '8 characters minimum', valid: isRightLength },
                            ].map(rule => (
                              <Grid item size={{xs:6}} key={rule.label}>
                                <Typography variant="body2" sx={{ color: "#5A6375", fontSize: 12, textAlign: "left", display: 'flex', alignItems: 'center' }}>
                                  {rule.valid ? <CheckCircleIcon sx={{ color: "#69cc63", mr: 0.5 }} /> : <CancelIcon sx={{ color: "#ff4c4c", mr: 0.5 }} />} {rule.label}
                                </Typography>
                              </Grid>
                            ))}
                          </Grid>
                        </Paper>
                      )}
                      <TextField
                        {...textFieldProps}
                        id="password"
                        type="password"
                        placeholder="Password"
                        autoComplete="off"
                        value={password}
                        onChange={handleChange("password")}
                        required
                      />
                      <Typography variant="body1" sx={{ color: "#202A3E", fontSize: 14, mb: '4px', mt: '18px' }}>
                        Confirm password
                      </Typography>
                      <TextField
                        {...textFieldProps}
                        id="passwordcon"
                        type="password"
                        placeholder="Confirm Password"
                        autoComplete="off"
                        value={passwordcon}
                        onChange={handleChange("passwordcon")}
                        required
                      />

                      <Button
                        variant="contained"
                        sx={{ mt: { xs: 2, sm: 2 }, mb: { xs: '14px', sm: 2 } }}
                        size="large"
                        color="primary"
                        type="submit"
                        fullWidth
                      >
                        Send
                      </Button>
                    </Box>
                  </FormContainer>
                )}
              </Grid>
            </Grid>
          </LoginPaper>
        </Grid>
      </Grid>

      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        open={open}
        sx={{ mt: '10px' }}
        onClose={handleClose}
        autoHideDuration={3000}
      >
        <SnackbarContent
          sx={{ backgroundColor: (theme) => theme.palette.error.dark }}
          message={<span>{error}</span>}
        />
      </Snackbar>
    </RootContainer>
  );
};

export default Forget;
